import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../styles/components/common/TagSelector.css';
import useApi from '../../Api';
import { FaTimes, FaPlus } from 'react-icons/fa';
import { validateTextInput } from '../common/TextValidator';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

const TagSelector = ({ selectedTags, setSelectedTags, maxTags = 10 }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionContainerRef = useRef(null);
  const apiClient = useApi();

  const MAX_TAG_LENGTH = 20;

  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      try {
        const response = await apiClient.get('/tags/search', {
          params: { q: query },
        });
        const suggestionNames = response.data.map((tag) => tag.name);
        setSuggestions(suggestionNames);
        setError(null);
      } catch (err) {
        console.error('Error fetching tag suggestions:', err);
        if (err.response && err.response.data && err.response.data.detail) {
          setError(err.response.data.detail);
        } else {
          setError('An unexpected error occurred while fetching tags.');
        }
        setSuggestions([]);
      }
    }, 300),
    [apiClient]
  );

  const debouncedValidate = useCallback(
    debounce((value) => {
      const { sanitizedText, errors } = validateTextInput(value, {
        maxLength: MAX_TAG_LENGTH,
        allowSpecialChars: false,
      });
      if (errors.length > 0) {
        setError(errors.join(' '));
        setSuggestions([]);
      } else {
        setError(null);
        fetchSuggestions(sanitizedText);
      }
    }, 150),
    [fetchSuggestions]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setHighlightedIndex(-1);
    if (value.trim() === '') {
      setError(null);
      setSuggestions([]);
      fetchSuggestions.cancel();
      debouncedValidate.cancel();
      return;
    }
    debouncedValidate(value);
  };

  const addTag = (tag) => {
    if (selectedTags.length >= maxTags) return;
    const sanitizedTag = tag.trim();
    const { isValid, errors } = validateTextInput(sanitizedTag, {
      maxLength: MAX_TAG_LENGTH,
      allowSpecialChars: false,
    });
    if (!isValid) {
      setError(errors.join(' '));
      return;
    }
    if (selectedTags.includes(sanitizedTag)) {
      toast.error('Tag already exists.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    setSelectedTags([...selectedTags, sanitizedTag]);
    setInputValue('');
    setSuggestions([]);
    setIsInputVisible(false);
    setError(null);
    setHighlightedIndex(-1);
    fetchSuggestions.cancel();
    debouncedValidate.cancel();
  };

  const removeTag = (tag) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredSuggestions.length === 0) return;
      setHighlightedIndex((prev) => (prev + 1) % filteredSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredSuggestions.length === 0) return;
      setHighlightedIndex((prev) =>
        prev <= 0 ? filteredSuggestions.length - 1 : prev - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex !== -1) {
        addTag(filteredSuggestions[highlightedIndex]);
      } else {
        const trimmedValue = inputValue.trim();
        if (trimmedValue) {
          addTag(trimmedValue);
        }
      }
    } else if (e.key === 'Escape') {
      setIsInputVisible(false);
      setInputValue('');
      setError(null);
    }
  };

  const handleAddButtonClick = () => {
    setIsInputVisible(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  useEffect(() => {
    return () => {
      fetchSuggestions.cancel();
      debouncedValidate.cancel();
    };
  }, [fetchSuggestions, debouncedValidate]);

  useEffect(() => {
    if (!isInputVisible) return;
    const handleClickOutside = (event) => {
      if (
        suggestionContainerRef.current &&
        !suggestionContainerRef.current.contains(event.target)
      ) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isInputVisible]);

  useEffect(() => {
    if (highlightedIndex !== -1 && suggestionContainerRef.current) {
      const highlightedItem = suggestionContainerRef.current.querySelector(`#suggestion-${highlightedIndex}`);
      if (highlightedItem) {
        highlightedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  const filteredSuggestions = suggestions.filter(
    (suggestion) => !selectedTags.includes(suggestion)
  );

  const activeDescendant =
    highlightedIndex !== -1 ? `suggestion-${highlightedIndex}` : '';

  return (
    <div className="tag-selector">
      <div className="selected-tags">
        {selectedTags.map((tag, index) => (
          <span className="tag-chip" key={index}>
            {tag}
            <FaTimes className="remove-tag-icon" onClick={() => removeTag(tag)} />
          </span>
        ))}
        {selectedTags.length < maxTags && (
          <div className="tag-add-container">
            {isInputVisible ? (
              <div ref={suggestionContainerRef}>
                <div className="tag-input-wrapper">
                  <input
                    type="text"
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Add a tag"
                    maxLength={MAX_TAG_LENGTH}
                    aria-label="Tag input"
                    aria-activedescendant={activeDescendant}
                    className={`tag-input ${error ? 'input-error' : ''}`}
                    onBlur={() => {
                      if (!inputValue) {
                        setIsInputVisible(false);
                        setError(null);
                      }
                    }}
                  />
                  {filteredSuggestions.length > 0 && (
                    <ul className="suggestions-list" role="listbox">
                      {filteredSuggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          id={`suggestion-${index}`}
                          role="option"
                          aria-selected={index === highlightedIndex}
                          className={index === highlightedIndex ? 'highlighted' : ''}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          onClick={() => addTag(suggestion)}
                        >
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  )}
                  {error && <span className="error-text" aria-live="assertive">{error}</span>}
                </div>
              </div>
            ) : (
              <span
                className="tag-chip tag-add-button"
                onClick={handleAddButtonClick}
                role="button"
                aria-label="Add tag"
                tabIndex="0"
              >
                <FaPlus className="add-tag-icon" style={{ marginRight: '4px' }} />
                <span className="add-tag-text">Tag</span>
              </span>
            )}
          </div>
        )}
      </div>
      {error && !isInputVisible && (
        <div className="error-message" aria-live="assertive">
          {error}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
